import { ROTransaction, RWTransaction } from '../../../db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  createObj,
  enumGetKeys,
  enumGetValues,
  throwObjectNotFound,
  trCore,
} from '../../../common'
import {
  CitizenCategoriesCoefficients,
  CitizenCategoriesCoefficientsRepository,
  CitizenCategoriesCoefficientsService,
  CitizenCategoryCoefficient,
} from './citizenCategoriesCoefficients'
import { runWithROTransaction } from '../../../db'
import { Permissions } from '../../authorization'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { CitizenCategory } from '../../citizens'

const CITIZEN_CATEGORIES_ID = '2d01b143e0a44fa592790c7b7251eadc'

enum EventType {
  CITIZEN_CATEGORY_COEFFICIENT_UPDATE = 'CITIZEN_CATEGORY_COEFFICIENT_UPDATE',
  CITIZEN_CATEGORY_COEFFICIENT_MERGE = 'CITIZEN_CATEGORY_COEFFICIENT_MERGE',
}

export const DEFAULT_COEFFICIENTS: CitizenCategoriesCoefficients = {
  AB_F: 1.06,
  AB_M: 1.06,
  P_F: 0.9,
  P_M: 0.9,
  CH: 0.85,
}

type UpdateCitizenCategoryCoefficientPayload = {
  category: CitizenCategory
  coefficient: CitizenCategoryCoefficient
}

type MergeCitizenCategoryCoefficientPayload = Partial<Record<CitizenCategory, CitizenCategoryCoefficient>>

interface DBCitizenCategoriesCoefficientsOpts extends BaseDBServiceOpts<CoreDict> {
  citizenCategoriesCoefficientsRepository: CitizenCategoriesCoefficientsRepository
}

export class DBCitizenCategoriesCoefficientsService
  extends BaseDBService<DBCitizenCategoriesCoefficientsOpts, CoreDict>
  implements CitizenCategoriesCoefficientsService
{
  constructor(opts: DBCitizenCategoriesCoefficientsOpts) {
    super('DBCitizenCategoriesCoefficientsService', opts)
    this.logger.debug({}, '<0262cf2c> Initialized')
  }

  async get(ctx: Context): Promise<CitizenCategoriesCoefficients> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.CITIZEN_CATEGORIES_VIEW)

    const storedCoefficients = (
      await runWithROTransaction(this.opts.dbConnection, tx =>
        this.opts.citizenCategoriesCoefficientsRepository.load(tx),
      )
    )?.coefficients

    const citizenCategoriesCoefficients: Partial<CitizenCategoriesCoefficients> = {}

    enumGetKeys(CitizenCategory).forEach(p => {
      const c = p as CitizenCategory
      citizenCategoriesCoefficients[c] = storedCoefficients?.[c] ?? DEFAULT_COEFFICIENTS[c]
    })

    return citizenCategoriesCoefficients as CitizenCategoriesCoefficients
  }

  async updateByCitizenCategory(
    ctx: Context,
    citizenCategory: CitizenCategory,
    coefficient: CitizenCategoryCoefficient,
  ): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.CITIZEN_CATEGORIES_MANAGE)

    if (!enumGetKeys(CitizenCategory).includes(citizenCategory)) {
      throwObjectNotFound(
        trCore(d => d.citizenCategories.categoryDoesNotExist, citizenCategory),
        { uid: '<4c660c83>' },
      )
    }

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW_OR_UPDATE,
        entityId: CITIZEN_CATEGORIES_ID,
        type: EventType.CITIZEN_CATEGORY_COEFFICIENT_UPDATE,
        payload: createObj<UpdateCitizenCategoryCoefficientPayload>({
          category: citizenCategory,
          coefficient: coefficient,
        }),
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }
}

export class CitizenCategoriesCoefficientsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      citizenCategoriesCoefficientsRepository: CitizenCategoriesCoefficientsRepository
      eventCreator: ModelEventCreator
    },
  ) {}

  // UPDATED
  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const storedCoefficients = (await this.opts.citizenCategoriesCoefficientsRepository.load(tx)) ?? {
      coefficients: {},
    }
    return await this.opts.eventCreator.create(ctx, tx, {
      entityId: entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.CITIZEN_CATEGORY_COEFFICIENT_MERGE,
      payload: createObj<MergeCitizenCategoryCoefficientPayload>(storedCoefficients.coefficients),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.CITIZEN_CATEGORY_COEFFICIENT_UPDATE: {
        const p = event.payload as UpdateCitizenCategoryCoefficientPayload
        const storedCoefficients = (await this.opts.citizenCategoriesCoefficientsRepository.load(tx)) ?? {
          coefficients: {},
        }

        storedCoefficients.coefficients[p.category] = p.coefficient

        await this.opts.citizenCategoriesCoefficientsRepository.save(tx, storedCoefficients)
        return true
      }
      case EventType.CITIZEN_CATEGORY_COEFFICIENT_MERGE: {
        const p = event.payload as MergeCitizenCategoryCoefficientPayload
        await this.opts.citizenCategoriesCoefficientsRepository.save(tx, { coefficients: p })
        return true
      }
    }
    return false
  }
}
